<template>
  <!--
    `persistent` y sin botón de cerrar a propósito: el registro está a medio
    camino y cerrar el modal dejaría al asistente creyendo que ya tiene cuenta.
    Las dos únicas salidas son verificar el código o corregir el correo.
  -->
  <v-dialog
    :value="value"
    persistent
    no-click-animation
    max-width="480px"
    content-class="tw-rounded-3xl"
  >
    <v-card class="tw-rounded-3xl tw-border-0 tw-shadow-md">
      <v-container class="tw-px-6 tw-py-6">
        <h2 class="tw-text-xl tw-font-semibold tw-leading-6 tw-text-gray-900">
          {{ $t('pages.registry.verify_email.title') }}
        </h2>
        <p class="tw-mt-3 tw-mb-0 tw-text-sm tw-text-gray-500">
          {{ $t('pages.registry.verify_email.description') }}
        </p>
        <p class="tw-mt-1 tw-mb-0 tw-break-all tw-text-sm tw-font-semibold tw-text-gray-900">
          {{ email }}
        </p>
      </v-container>

      <v-divider class="tw-border-gray-100" />

      <v-container class="tw-px-6 tw-py-4">
        <div class="tw-relative">
          <v-otp-input
            v-model="code"
            length="6"
            type="number"
            :disabled="loading"
            @finish="submit"
          />
          <v-overlay absolute :value="loading">
            <v-progress-circular indeterminate color="primary" />
          </v-overlay>
        </div>

        <v-alert
          v-if="error"
          dense
          text
          type="error"
          class="tw-mt-2 tw-mb-0 tw-text-sm"
        >
          {{ error }}
        </v-alert>

        <p v-if="resent" class="tw-mt-2 tw-mb-0 tw-text-sm tw-text-green-700">
          {{ $t('pages.registry.verify_email.resent') }}
        </p>
      </v-container>

      <v-divider class="tw-border-gray-100" />

      <v-container class="tw-px-6 tw-py-4">
        <!--
          No hay botón de confirmar: el campo se envía solo al sexto dígito
          (`@finish`), así que el botón nunca alcanzaba un estado en el que
          sirviera para algo.
        -->
        <div class="tw-flex tw-flex-wrap tw-justify-between tw-gap-2">
          <!--
            "Perdí el código": reenviar. El contador evita que el botón se
            convierta en una forma de hacernos mandar correos en cadena.
          -->
          <AppBtn
            color="transparent"
            small
            :disabled="cooldown > 0 || loading"
            @click="resend"
          >
            {{
              cooldown > 0
                ? $t('pages.registry.verify_email.resend_in', { seconds: cooldown })
                : $t('pages.registry.verify_email.resend')
            }}
          </AppBtn>

          <AppBtn color="transparent" small :disabled="loading" @click="editEmail">
            {{ $t('pages.registry.verify_email.edit_email') }}
          </AppBtn>
        </div>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import AppBtn from '@/components/App/AppBtn.vue'

export default {
  name: 'RegistryVerifyEmailModal',
  components: {
    AppBtn,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    email: {
      type: String,
      default: '',
    },
    formUuid: {
      type: String,
      default: '',
    },
    // Segundos que el backend pide esperar antes del próximo reenvío.
    resendSeconds: {
      type: Number,
      default: 60,
    },
  },
  data: () => ({
    code: '',
    loading: false,
    error: '',
    resent: false,
    cooldown: 0,
    timer: null,
  }),
  computed: {
    isComplete() {
      return this.code.length === 6
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.reset()
        this.startCooldown()
        return
      }
      this.stopCooldown()
    },
  },
  beforeDestroy() {
    this.stopCooldown()
  },
  methods: {
    reset() {
      this.code = ''
      this.error = ''
      this.resent = false
    },
    submit() {
      if (!this.isComplete || this.loading) {
        return
      }
      this.loading = true
      this.error = ''
      this.resent = false

      this.$store
        .dispatch('session/confirmVerificationCode', {
          uuid: this.formUuid,
          email: this.email,
          code: this.code,
        })
        .then((data) => {
          this.$emit('verified', data.verification_token)
        })
        .catch((err) => {
          this.error = err
          // Se limpia el campo: dejar los seis dígitos errados obliga a
          // borrarlos uno por uno antes de reintentar.
          this.code = ''
        })
        .finally(() => {
          this.loading = false
        })
    },
    resend() {
      if (this.cooldown > 0 || this.loading) {
        return
      }
      this.loading = true
      this.error = ''
      this.resent = false

      this.$store
        .dispatch('session/requestVerificationCode', {
          uuid: this.formUuid,
          email: this.email,
        })
        .then((data) => {
          this.code = ''
          this.resent = true
          this.startCooldown(data.resend_in)
        })
        .catch((err) => {
          this.error = err
        })
        .finally(() => {
          this.loading = false
        })
    },
    editEmail() {
      this.stopCooldown()
      this.$emit('edit-email')
    },
    startCooldown(seconds) {
      this.stopCooldown()
      this.cooldown = seconds || this.resendSeconds
      this.timer = setInterval(() => {
        this.cooldown -= 1
        if (this.cooldown <= 0) {
          this.stopCooldown()
        }
      }, 1000)
    },
    stopCooldown() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      this.cooldown = 0
    },
  },
}
</script>
